import { Component, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TextSizeService } from './text-size.service';
import { I18nService } from '../i18n/i18n.service';

/**
 * A − / A + pair for text size.
 *
 * The buttons carry a literal "A" at two sizes rather than bare +/− symbols: a shopper scanning a busy
 * header needs to know WHAT gets bigger, and "A" says "text" in any language, which matters on a screen
 * that may be in Urdu. The percentage is exposed to screen readers via aria-label, not printed, so the
 * control stays small.
 *
 * Sized in `em`, not `rem`, on purpose — otherwise the control would grow as you press it, which looks
 * like a bug and pushes the rest of the header around.
 */
@Component({
  selector: 'app-text-size-control',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div [class]="variant() === 'drawer' ? 'ts-row' : 'ts-pill'"
         role="group" [attr.aria-label]="i18n.t('text.size')">
      @if (variant() === 'drawer') {
        <span class="ts-row__label">
          <span class="ts-row__ico" aria-hidden="true">🔤</span>
          <span class="ts-row__text">{{ i18n.t('text.size') }}</span>
        </span>
      }
      <div class="ts-btns">
        <button type="button" class="ts-btn ts-btn--dec"
                [disabled]="!svc.canDecrease()"
                [attr.aria-label]="i18n.t('text.smaller')"
                (click)="svc.decrease()">A−</button>
        <!-- Tapping the readout resets to the default — a way back that needs no third button. -->
        <button type="button" class="ts-btn ts-btn--val"
                [disabled]="!svc.isCustom()"
                [attr.aria-label]="i18n.t('text.reset')"
                [title]="i18n.t('text.reset')"
                (click)="svc.reset()">{{ svc.percent() }}%</button>
        <button type="button" class="ts-btn ts-btn--inc"
                [disabled]="!svc.canIncrease()"
                [attr.aria-label]="i18n.t('text.larger')"
                (click)="svc.increase()">A+</button>
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }

    /* Header placement — translucent pill on the red bar, matching Install and the language toggle. */
    .ts-pill {
      display: inline-flex; align-items: center; flex: 0 0 auto;
      border: 1px solid rgba(255, 255, 255, 0.45); background: rgba(255, 255, 255, 0.12);
      border-radius: 999px; padding: 2px;
    }
    .ts-pill .ts-btn {
      border: none; background: transparent; cursor: pointer; font: inherit; color: #fff;
      font-weight: 800; padding: 0.28rem 0.5rem; border-radius: 999px; white-space: nowrap;
      /* em, not rem: this control must not resize itself as it is used. */
      font-size: 0.8em;
    }
    .ts-pill .ts-btn--val { font-size: 0.68em; font-weight: 700; opacity: 0.85; min-width: 3.1em; }
    .ts-pill .ts-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.22); }
    .ts-pill .ts-btn:disabled { opacity: 0.45; cursor: default; }

    /* Drawer placement — a labelled row matching .oy-drawer__links a.
       flex-wrap matters: at the same 0.6rem padding the Urdu label ("لکھائی کا سائز") plus the
       fixed-width stepper does not fit 300px, and without wrapping the label broke onto two lines
       INSIDE its own cell. Now the control drops below the label instead. */
    .ts-row {
      display: flex; align-items: center; justify-content: space-between;
      gap: 0.6rem; row-gap: 0.4rem; flex-wrap: wrap;
      width: 100%; padding: 0.75rem 0.6rem;
    }
    /* The 1.5rem icon box is shared with .oy-drawer__links a — emoji glyph widths differ, so without a
       fixed box every row's label starts at a different x and the menu reads as ragged. */
    .ts-row__label {
      display: flex; align-items: center; gap: 0.7rem; min-width: 0;
      font-size: 1rem; font-weight: 600; color: var(--color-text, #17120f);
    }
    .ts-row__ico { flex: 0 0 1.5rem; text-align: center; font-size: 1rem; line-height: 1; }
    .ts-row__text { min-width: 0; }
    .ts-row .ts-btns { display: inline-flex; border: 1px solid #ece3e3; border-radius: 999px; padding: 2px; }
    .ts-row .ts-btn {
      border: none; background: transparent; cursor: pointer; font: inherit;
      font-weight: 800; color: var(--color-text, #17120f);
      padding: 0.3rem 0.6rem; border-radius: 999px; font-size: 0.9em;
    }
    .ts-row .ts-btn--val { font-size: 0.75em; font-weight: 700; color: var(--color-text-muted, #6b6b6b); min-width: 3.2em; }
    .ts-row .ts-btn:hover:not(:disabled) { background: #faf7f7; }
    .ts-row .ts-btn:disabled { opacity: 0.45; cursor: default; }

    .ts-btns { display: inline-flex; align-items: center; }

    /* "A−" and "A+" are a Latin letter plus a NEUTRAL sign, so inside RTL the pair reorders and they
       render as "−A" / "+A". Pin them LTR — verified in the Urdu drawer, where they were mirrored. */
    .ts-btn { direction: ltr; unicode-bidi: isolate; }

    /* The header has no room for this under 1080px — the drawer row takes over, as with the other
       pills. MUST match ordering-layout.css's header breakpoint, else it shows in both at once.
       Moved 860 → 1080 on 2026-09-22 with the layout. */
    @media (max-width: 1080px) {
      .ts-pill { display: none; }
    }
  `],
})
export class TextSizeControlComponent {
  readonly variant = input<'header' | 'drawer'>('header');
  protected readonly svc = inject(TextSizeService);
  protected readonly i18n = inject(I18nService);
}
